import { SHARE_STATE_TOKEN_QUERY_PARAM } from '@repo/map-user-data/share-state';
import type { FilterState } from '@repo/store/maps/slices/mapData';
import { CUSTOM_PINS_CATEGORY_ID, VIDEO_PINS_SUBCATEGORY_ID } from '@repo/store/maps/utils/mapData';
import type { GroupedCategory, MapData } from '@repo/types/maps/map-types';

export type UrlFilterState = Partial<FilterState> & {
    visibleCategories?: string[];
    allCategories?: string[];
};

const FILTER_URL_PARAM_NAMES = [
    'visibleCategories',
    'visibleCategory',
    'visibleSubcategories',
    'visibleSubcategory',
    'visibleRegions',
    'visibleRegion',
    'showOnlyRegions',
    'allCategories',
    'hiddenCategories',
    'hiddenSubcategories',
    'hideAll',
    'teleportOnly',
    'unfoundOnly',
    'search',
    SHARE_STATE_TOKEN_QUERY_PARAM,
] as const;

function splitListParam(value: string | null): string[] {
    return value?.split(',').filter(Boolean) ?? [];
}

function areSetsEqual(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
    if (a.size !== b.size) return false;
    for (const value of a) {
        if (!b.has(value)) return false;
    }
    return true;
}

function getKnownSubcategoryIds(mapData: MapData): string[] {
    return [
        ...new Set([
            ...mapData.metadata.subcategories.map(subcategory => subcategory.externalId),
            CUSTOM_PINS_CATEGORY_ID,
            VIDEO_PINS_SUBCATEGORY_ID,
        ]),
    ];
}

function getCategorySubcategoryIds(mapData: MapData, categoryId: string): string[] {
    const ids = mapData.metadata.subcategories
        .filter(subcategory => subcategory.categoryExternalId === categoryId)
        .map(subcategory => subcategory.externalId);

    if (categoryId === CUSTOM_PINS_CATEGORY_ID) {
        ids.push(CUSTOM_PINS_CATEGORY_ID, VIDEO_PINS_SUBCATEGORY_ID);
    }

    return [...new Set(ids)];
}

function getGroupedSubcategoryIds(groupedCategories: readonly GroupedCategory[]): string[] {
    return [...new Set(groupedCategories.flatMap(category => category.subcategories.map(sub => sub.externalId)))];
}

export function parseMapFilterUrlState(searchParams: URLSearchParams, mapData: MapData): UrlFilterState | null {
    const filterState: UrlFilterState = {};

    const visibleSubcategoryParam = searchParams.get('visibleSubcategory');
    const visibleSubcategoriesParam = searchParams.get('visibleSubcategories');
    const visibleCategoryParam = searchParams.get('visibleCategory');

    if (visibleSubcategoryParam) {
        filterState.hiddenSubcategories = getKnownSubcategoryIds(mapData).filter(
            externalId => externalId !== visibleSubcategoryParam,
        );
    } else if (visibleSubcategoriesParam) {
        const visibleSubcategories = new Set(splitListParam(visibleSubcategoriesParam));
        filterState.hiddenSubcategories = getKnownSubcategoryIds(mapData).filter(
            externalId => !visibleSubcategories.has(externalId),
        );
    } else if (visibleCategoryParam) {
        const visibleSubcategories = new Set(getCategorySubcategoryIds(mapData, visibleCategoryParam));
        filterState.hiddenSubcategories = getKnownSubcategoryIds(mapData).filter(
            externalId => !visibleSubcategories.has(externalId),
        );
    } else {
        const visibleCategoriesParam = searchParams.get('visibleCategories');
        const allCategoriesParam = searchParams.get('allCategories');
        const hiddenCategoriesParam = searchParams.get('hiddenCategories');

        if (visibleCategoriesParam && allCategoriesParam) {
            const visibleCategories = splitListParam(visibleCategoriesParam);
            const allCategories = splitListParam(allCategoriesParam);
            const hiddenCategories = allCategories.filter(categoryId => !visibleCategories.includes(categoryId));

            if (hiddenCategories.length > 0) {
                filterState.hiddenSubcategories = mapData.metadata.subcategories
                    .filter(subcategory => hiddenCategories.includes(subcategory.categoryExternalId))
                    .map(subcategory => subcategory.externalId);
            }
        } else if (hiddenCategoriesParam) {
            const hiddenCategories = splitListParam(hiddenCategoriesParam);

            if (hiddenCategories.length > 0) {
                filterState.hiddenSubcategories = mapData.metadata.subcategories
                    .filter(subcategory => hiddenCategories.includes(subcategory.categoryExternalId))
                    .map(subcategory => subcategory.externalId);
            }
        }

        const hiddenSubcategoriesParam = searchParams.get('hiddenSubcategories');
        if (hiddenSubcategoriesParam) {
            filterState.hiddenSubcategories = splitListParam(hiddenSubcategoriesParam);
        }
    }

    const visibleRegionParam = searchParams.get('visibleRegion');
    const visibleRegionsParam = searchParams.get('visibleRegions');
    const showOnlyRegionsParam = searchParams.get('showOnlyRegions');
    const regions = mapData.regions?.features;
    if (regions?.length && (visibleRegionParam || visibleRegionsParam !== null)) {
        const visibleRegionIds = visibleRegionParam ? [visibleRegionParam] : splitListParam(visibleRegionsParam);
        const visibleRegionIdSet = new Set(visibleRegionIds);
        filterState.hiddenRegions = regions
            .map(region => region.properties.externalId)
            .filter(regionId => !visibleRegionIdSet.has(regionId));
        filterState.regionFilterMode = visibleRegionParam || showOnlyRegionsParam === 'true' ? 'include' : 'exclude';
    }

    if (searchParams.get('hideAll') === 'true') {
        filterState.hideAll = true;
    }

    if (searchParams.get('teleportOnly') === 'true') {
        filterState.teleportOnly = true;
    }

    if (searchParams.get('unfoundOnly') === 'true') {
        filterState.unfoundOnly = true;
    }

    const searchParam = searchParams.get('search');
    if (searchParam) {
        filterState.searchTerm = searchParam;
    }

    return Object.keys(filterState).length > 0 ? filterState : null;
}

export function appendMapFilterUrlParams(
    url: URL,
    {
        filterState,
        groupedCategories,
        mapData,
    }: {
        filterState: FilterState;
        groupedCategories: readonly GroupedCategory[];
        mapData?: MapData | null;
    },
): void {
    const allSubcategoryIds = getGroupedSubcategoryIds(groupedCategories);

    if (filterState.hideAll) {
        url.searchParams.set('hideAll', 'true');
    } else if (filterState.hiddenSubcategories.length > 0 && allSubcategoryIds.length > 0) {
        const hiddenSubcategoryIdSet = new Set(filterState.hiddenSubcategories);
        const visibleSubcategoryIds = allSubcategoryIds.filter(id => !hiddenSubcategoryIdSet.has(id));

        if (visibleSubcategoryIds.length === 1) {
            url.searchParams.set('visibleSubcategory', visibleSubcategoryIds[0] as string);
        } else {
            const visibleSubcategoryIdSet = new Set(visibleSubcategoryIds);
            const matchingCategory = groupedCategories.find(category => {
                const categorySubcategoryIds = category.subcategories.map(sub => sub.externalId);
                return (
                    categorySubcategoryIds.length > 0 &&
                    areSetsEqual(visibleSubcategoryIdSet, new Set(categorySubcategoryIds))
                );
            });

            if (matchingCategory) {
                url.searchParams.set('visibleCategory', matchingCategory.externalId);
            } else {
                const visibleSubcategoriesValue = visibleSubcategoryIds.join(',');
                const hiddenSubcategoriesValue = filterState.hiddenSubcategories.join(',');
                const param =
                    visibleSubcategoriesValue.length > 0 &&
                    visibleSubcategoriesValue.length < hiddenSubcategoriesValue.length
                        ? 'visibleSubcategories'
                        : 'hiddenSubcategories';
                url.searchParams.set(
                    param,
                    param === 'visibleSubcategories' ? visibleSubcategoriesValue : hiddenSubcategoriesValue,
                );
            }
        }
    } else if (filterState.hiddenSubcategories.length > 0) {
        url.searchParams.set('hiddenSubcategories', filterState.hiddenSubcategories.join(','));
    }

    const allRegionIds = mapData?.regions?.features.map(region => region.properties.externalId) ?? [];
    const hiddenRegionIdSet = new Set(filterState.hiddenRegions);
    const hiddenKnownRegionCount = allRegionIds.filter(regionId => hiddenRegionIdSet.has(regionId)).length;

    if (allRegionIds.length > 0 && (hiddenKnownRegionCount > 0 || filterState.regionFilterMode === 'include')) {
        const visibleRegionIds = allRegionIds.filter(regionId => !hiddenRegionIdSet.has(regionId));

        if (filterState.regionFilterMode === 'include' && visibleRegionIds.length === 1) {
            url.searchParams.set('visibleRegion', visibleRegionIds[0] as string);
        } else {
            url.searchParams.set('visibleRegions', visibleRegionIds.join(','));
            if (filterState.regionFilterMode === 'include') {
                url.searchParams.set('showOnlyRegions', 'true');
            }
        }
    }

    if (filterState.teleportOnly) {
        url.searchParams.set('teleportOnly', 'true');
    }

    if (filterState.unfoundOnly) {
        url.searchParams.set('unfoundOnly', 'true');
    }

    if (filterState.searchTerm) {
        url.searchParams.set('search', filterState.searchTerm);
    }
}

export function clearMapFilterUrlParams(url: URL): void {
    for (const param of FILTER_URL_PARAM_NAMES) {
        url.searchParams.delete(param);
    }
}
